
















import { computed, defineComponent, ref } from "@vue/composition-api";
import { ContentPriceSubject, ContentPriceSubjectTypeEnum } from "@scrile/api-provider/dist/api/ContentPriceProvider";
import { LikeSubjectType } from "@scrile/api-provider/dist/api/LikeProvider";
import { PaymentPackage } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { PurchaseSubjectTypeEnum } from "@scrile/api-provider/dist/api/PurchaseTypeProvider";
import { Message } from "@scrile/api-provider/dist/projects/scrileConnect/MessagesProvider";
import useOneTimePurchase from "@/hooks/useOneTimePurchase";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { formatMoneyString, getMainCurrencyCode } from "@/lib/moneyHelpers";
import { PurchaseDetails } from "@/payments/types";
import { BlockingStatus } from "@/types";
import { EVENTS } from "@/const";
import PaymentConfirmation from "@/components/PaymentConfirmation";
import { getMessageView } from "./views";

export default defineComponent({
  name: "MessageItem",
  components: { PaymentConfirmation },
  props: {
    message: { type: Object as () => Message, required: true },
    authUserId: { type: String, required: true },
    userId: { type: String, required: true },
    paymentPackages: { type: Array as () => PaymentPackage[], required: true },
    blockingStatus: { type: Object as () => BlockingStatus, required: true },
    markAsSeen: { type: Boolean, required: true },
  },
  setup(props) {
    const loading = ref(false);
    const viewComponent = computed(() => getMessageView(props.message));
    const data = computed<ContentPriceSubject>(() => ({
      subjectId: props.message.id,
      subjectType: ContentPriceSubjectTypeEnum.MESSAGE_ATTACHMENT,
    }));
    const cost = computed(
      () =>
        props.paymentPackages
          .find(p => p.id === props.message.attachmentPricePackageId)
          ?.costs?.find(c => c.currency === getMainCurrencyCode())?.cost
    );
    const purchaseDetails = computed<PurchaseDetails>(() => ({
      pricePackageId: props.message.attachmentPricePackageId ?? "0",
      price: (cost.value && formatMoneyString(cost.value, { currencyCode: getMainCurrencyCode() })) || undefined,
      subject: {
        subjectId: props.message.id,
        subjectType: PurchaseSubjectTypeEnum.MESSAGE_ATTACHMENT,
      },
    }));
    const { performOneTimePurchase, purchaseInfoDetails } = useOneTimePurchase(data, purchaseDetails);

    const purchase = async (onRequestEnd: Function) => {
      try {
        if (!props.message.attachmentPricePackageId) {
          return;
        }
        loading.value = true;
        const response = await performOneTimePurchase();
        if (response.success) {
          emitter.emit(EVENTS.MESSAGES_UPDATE_MESSAGE, props.message.id);
          emitter.emit(EVENTS.NOTIFICATION_SHOW, {
            text: $t("One time buy {0} content for {1}", [
              props.message.senderUser?.screenName,
              cost.value ? formatMoneyString(cost.value, { currencyCode: getMainCurrencyCode() }) : "0",
            ]),
            image: {
              user: props.message.senderUser,
            },
            button: {
              text: $t("Close"),
              cb: () => {},
            },
            position: "top",
          });
          onRequestEnd(true);
        }
      } catch (e) {
        onRequestEnd(false);
      } finally {
        loading.value = false;
      }
    };
    const toggleLike = async () => {
      await providers.LikeProvider.toggle({
        subject: { subjectId: props.message.id, subjectType: LikeSubjectType.MESSAGE },
      });
      emitter.emit(EVENTS.MESSAGES_LIKED_MESSAGE, { messageId: props.message.id });
    };
    return { loading, viewComponent, purchaseInfoDetails, purchase, toggleLike };
  },
  mounted() {
    this.$emit("rendered", { el: this.$el });
  },
});
